import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { defaultVisitors } from '@genericBookings/types/Visitors';
import API from '@widgets/shared/API/connection';
import visitorCountFromSchema, { visitorsFromSchema } from '@genericBookings/visitorUtils';
export const initialActivityState = {
    value: [],
    requestingData: false,
    requestingActivities: false,
    requestingProductBands: false,
    requestingPriceBands: false,
    requestingSessions: false,
    fetchedActivities: false,
    fetchedProductBands: false,
    fetchedPriceBands: false,
    fetchedSessions: false,
};
const activityIndexById = (activityId, activities) => (activities).findIndex((searchActivity) => searchActivity.activity.id === activityId);
export const getActivitiesForDate = createAsyncThunk('activities/fetchActivitiesForDate', async (data, thunkAPI) => {
    const { productId, date, paramSessions } = data;
    const newActivities = await API.getActivitiesAvailableForDate(productId, date);
    return newActivities.map((theActivity) => {
        let selected = false;
        let paramVisitors = defaultVisitors;
        if (paramSessions) {
            const foundParam = paramSessions.find((theSession) => theSession.ActivityId === theActivity.id);
            if (foundParam) {
                paramVisitors = visitorsFromSchema(foundParam);
                selected = visitorCountFromSchema(foundParam) > 0 || typeof foundParam.SessionId === 'number';
            }
        }
        return {
            activity: theActivity,
            visitors: paramVisitors,
            activeSession: undefined,
            sessions: [],
            selected,
        };
    });
});
export const readProductDetails = createAsyncThunk('activities/fetchActivityDetails', async (data, thunkAPI) => {
    const { activities, date } = data;
    console.log(data);
    const activitiesWithProductBands = await Promise.all(activities.map(async (activity) => ({
        ...activity,
        activity: {
            ...activity.activity,
            productBands: await API.readProduct(activity.activity.id),
        },
    })));
    return Promise.all(activitiesWithProductBands.map(async (activity) => ({
        ...activity,
        activity: {
            ...activity.activity,
            priceBands: await API.readPrices(activity.activity.id, date),
        },
    })));
});
export const readProducts = createAsyncThunk('activities/readProduct', async (activities, thunkAPI) => {
    const fetchingActivities = activities.map(async (activity) => ({
        productBands: await API.readProduct(activity.activity.id),
        activity,
    }));
    const allActivities = await Promise.all(fetchingActivities);
    return allActivities.map(({ activity: resActivity, productBands }) => {
        const foundActivity = activityIndexById(resActivity.activity.id, activities);
        if (foundActivity !== -1) {
            const activeActivity = activities[foundActivity];
            if (activeActivity) {
                return {
                    activity: {
                        ...activities[foundActivity].activity,
                        productBands,
                    },
                    sessions: activeActivity.sessions,
                    activeSession: activeActivity.activeSession,
                    visitors: activeActivity.visitors,
                    selected: activeActivity.selected,
                };
            }
        }
        return resActivity;
    });
});
export const readPrices = createAsyncThunk('activities/readPrices', async (data, thunkAPI) => {
    const { activities, date } = data;
    const fetchingActivities = activities.map(async (activity) => ({
        priceBands: await API.readPrices(activity.activity.id, date),
        activity,
    }));
    const allActivities = await Promise.all(fetchingActivities);
    return allActivities.map(({ priceBands, activity: resActivity }) => {
        const foundActivity = activityIndexById(resActivity.activity.id, activities);
        if (foundActivity !== -1) {
            const activeActivity = activities[foundActivity];
            if (activeActivity) {
                return {
                    activity: {
                        ...activities[foundActivity].activity,
                        priceBands,
                    },
                    sessions: activeActivity.sessions,
                    activeSession: activeActivity.activeSession,
                    visitors: activeActivity.visitors,
                    selected: activeActivity.selected,
                };
            }
        }
        return resActivity;
    });
});
export const readSessions = createAsyncThunk('activities/readSessions', async (data, thunkAPI) => {
    const { activities, date, visitorsOverride, isMulti, productId, values, } = data;
    const activitiesToUse = activities;
    const fetchingActivities = activitiesToUse.map(async (activity) => {
        const activityVisitors = visitorsOverride ?? activity.visitors ?? defaultVisitors;
        return {
            sessions: isMulti
                ? await API.getSessionsById(activity.activity.id, productId, date, activityVisitors)
                : await API.getSessions(values),
            activity,
        };
    });
    const allActivities = await Promise.all(fetchingActivities);
    // all warnings, including duplicates
    return allActivities.map(({ sessions: resSessions, activity: resActivity }) => {
        const foundActivity = activityIndexById(resActivity.activity.id, activitiesToUse);
        console.log('useActivities - got sessions: ', resSessions);
        if (foundActivity !== -1) {
            const activeActivity = activitiesToUse[foundActivity];
            if (activeActivity) {
                console.log('useActivities - found activity: ', activeActivity);
                return {
                    activity: {
                        ...activitiesToUse[foundActivity]?.activity,
                        sessions: resSessions,
                    },
                    sessions: resSessions,
                    activeSession: activeActivity.activeSession,
                    visitors: activeActivity.visitors,
                    selected: activeActivity.selected,
                };
            }
        }
        return resActivity;
    });
    // return formattedActivities;
});
export const activitySlice = createSlice({
    name: 'activityReducer',
    initialState: initialActivityState,
    reducers: {
        addActivity: (state, action) => {
            state.value.push(action.payload);
        },
        clearActivities: (state) => {
            state.value = [];
        },
        setActivitySessions: (state, action) => {
            console.log('Setting activities to: ', action.payload);
            state.value = action.payload;
        },
        resetFetchedAll: (state, action) => {
            state.fetchedSessions = false;
            state.fetchedProductBands = false;
            state.fetchedPriceBands = false;
            state.fetchedActivities = false;
            state.requestingData = false;
        },
        setSessionsForActivity: (state, action) => {
            const activityIdx = state.value.findIndex((thisActivity) => thisActivity.activity.id === action.payload.currActivity.activity.id);
            if (state.value[activityIdx] !== undefined) {
                state.value[activityIdx].sessions = action.payload.sessions;
            }
        },
        setActiveSessionForActivity: (state, action) => {
            const activityIdx = state.value.findIndex((thisActivity) => thisActivity.activity.id === action.payload.currActivity.activity.id);
            if (state.value[activityIdx] !== undefined) {
                state.value[activityIdx].activeSession = action.payload.session;
            }
        },
        setSelectedForActivity: (state, action) => {
            const activityIdx = state.value.findIndex((thisActivity) => thisActivity.activity.id === action.payload.currActivity.activity.id);
            if (state.value[activityIdx] !== undefined) {
                state.value[activityIdx].selected = action.payload.selected;
            }
        },
        setVisitorsForActivity: (state, action) => {
            const activityIdx = state.value.findIndex((thisActivity) => thisActivity.activity.id === action.payload.currActivity.activity.id);
            if (state.value[activityIdx] !== undefined) {
                state.value[activityIdx].visitors = action.payload.visitors;
            }
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getActivitiesForDate.pending, (state, action) => {
            state.requestingData = true;
            state.requestingActivities = true;
            state.fetchedActivities = false;
        });
        builder.addCase(getActivitiesForDate.fulfilled, (state, action) => {
            state.requestingData = false;
            state.requestingActivities = false;
            state.fetchedActivities = true;
            state.value = action.payload;
        });
        builder.addCase(getActivitiesForDate.rejected, (state, action) => {
            state.requestingData = false;
            state.requestingActivities = false;
            state.fetchedActivities = true;
        });
        builder.addCase(readProductDetails.pending, (state, action) => {
            state.requestingData = true;
            state.requestingProductBands = true;
            state.requestingPriceBands = true;
            state.fetchedPriceBands = false;
            state.fetchedProductBands = false;
        });
        builder.addCase(readProductDetails.fulfilled, (state, action) => {
            state.requestingData = false;
            state.requestingProductBands = false;
            state.fetchedProductBands = true;
            state.requestingPriceBands = false;
            state.fetchedPriceBands = true;
            state.value = action.payload;
        });
        builder.addCase(readProductDetails.rejected, (state, action) => {
            state.requestingData = false;
            state.requestingProductBands = false;
            state.requestingPriceBands = false;
            state.fetchedPriceBands = true;
            state.fetchedProductBands = true;
        });
        builder.addCase(readProducts.pending, (state, action) => {
            state.requestingData = true;
            state.requestingProductBands = true;
            state.fetchedProductBands = false;
        });
        builder.addCase(readProducts.fulfilled, (state, action) => {
            state.requestingData = false;
            state.requestingProductBands = false;
            state.fetchedProductBands = true;
            state.value = action.payload;
        });
        builder.addCase(readProducts.rejected, (state, action) => {
            state.requestingData = false;
            state.requestingProductBands = false;
            state.fetchedProductBands = true;
        });
        builder.addCase(readPrices.pending, (state, action) => {
            state.requestingData = true;
            state.requestingPriceBands = true;
            state.fetchedPriceBands = false;
        });
        builder.addCase(readPrices.fulfilled, (state, action) => {
            state.requestingData = false;
            state.requestingPriceBands = false;
            state.fetchedPriceBands = true;
            state.value = action.payload;
        });
        builder.addCase(readPrices.rejected, (state, action) => {
            state.requestingData = false;
            state.requestingPriceBands = false;
            state.fetchedPriceBands = true;
        });
        builder.addCase(readSessions.pending, (state, action) => {
            state.requestingData = true;
            state.requestingSessions = true;
            state.fetchedSessions = false;
        });
        builder.addCase(readSessions.fulfilled, (state, action) => {
            state.requestingData = false;
            state.requestingSessions = false;
            state.fetchedSessions = true;
            state.value = action.payload;
        });
        builder.addCase(readSessions.rejected, (state, action) => {
            state.requestingData = false;
            state.requestingSessions = false;
            state.fetchedSessions = true;
        });
    },
});
export const { addActivity, clearActivities, setActivitySessions, setSessionsForActivity, setActiveSessionForActivity, setVisitorsForActivity, setSelectedForActivity, resetFetchedAll, } = activitySlice.actions;
export const getActivities = (state) => state.activities.value;
export const getSelectedActivitiesWithSession = (state) => state.activities.value.filter((activity) => activity.selected && activity.activeSession !== undefined);
export const setActivities = (value) => (dispatch) => Promise.resolve(dispatch(setActivitySessions(value)));
export const setTheSessionsForActivity = (sessions, activity) => (dispatch) => Promise.resolve(dispatch(setSessionsForActivity({ currActivity: activity, sessions })));
export const setTheActiveSessionForActivity = (activity, session) => (dispatch) => Promise.resolve(dispatch(setActiveSessionForActivity({ currActivity: activity, session })));
export const getSelectedActivities = (state) => state.activities.value.filter((activity) => activity.selected);
export const getUnselectedActivities = (state) => state.activities.value.filter((activity) => !activity.selected);
export const getFetchingStatus = ({ activities: { fetchedActivities, fetchedSessions, fetchedPriceBands, fetchedProductBands, requestingActivities, requestingData, requestingSessions, requestingPriceBands, requestingProductBands, }, }) => ({
    fetchedActivities,
    fetchedSessions,
    fetchedPriceBands,
    fetchedProductBands,
    requestingActivities,
    requestingData,
    requestingSessions,
    requestingPriceBands,
    requestingProductBands,
});
export const setTheVisitorsForActivity = (visitors, activity) => (dispatch) => Promise.resolve(dispatch(setVisitorsForActivity({ currActivity: activity, visitors })));
export const setIsSelectedForActivity = (selected, activity) => (dispatch) => Promise.resolve(dispatch(setSelectedForActivity({ currActivity: activity, selected })));
export default activitySlice.reducer;
